import { Link } from "react-router-dom";
import { Shield, Eye, Lock, Database, Globe, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = "January 21, 2026";
  const effectiveDate = "January 21, 2026";
  return <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black backdrop-blur-md border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
            <span>Last Updated: {lastUpdated}</span>
            <span>•</span>
            <span>Effective Date: {effectiveDate}</span>
          </div>
        </div>

        {/* Introduction */}
        <section className="mb-10">
          <p className="text-lg text-gray-300 leading-relaxed">
            Metro City Trading (Pty) Ltd ("Metro City Groove", "we", "us", or "our") is committed to protecting 
            your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
            information when you use our platform, mobile applications, and services (collectively, the "Services").
          </p>
          <p className="text-gray-400 mt-4">
            By accessing or using our Services, you agree to this Privacy Policy. If you do not agree with the 
            terms of this policy, please do not access the Services.
          </p>
        </section>

        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h2 className="text-xl font-semibold mb-4">Contents</h2>
          <ul className="space-y-2 text-gray-400">
            {["Information We Collect", "How We Use Your Information", "Information Sharing and Disclosure", "Data Security", "Data Retention", "Your Rights and Choices", "International Data Transfers", "Children's Privacy", "Third-Party Services", "Changes to This Policy", "Contact Us"].map((item, index) => <li key={index}>
                <a href={`#section-${index + 1}`} className="hover:text-white transition-colors">
                  {index + 1}. {item}
                </a>
              </li>)}
          </ul>
        </nav>

        {/* Section 1 */}
        <section id="section-1" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">1. Information We Collect</h2>
          </div>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">1.1 Information You Provide</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong>Account Information:</strong> Name, email address, phone number, username, password, profile picture, and account preferences.</li>
                <li><strong>Profile Data:</strong> Bio, location, social media links, professional credentials (for creators and venues).</li>
                <li><strong>Payment Information:</strong> Banking details, payment card information (processed securely through our payment processors).</li>
                <li><strong>Communications:</strong> Messages, comments, and interactions with other users and our support team.</li>
                <li><strong>Content:</strong> Photos, videos, music, and other media you upload or create on our platform.</li>
                <li><strong>Verification Documents:</strong> ID documents and business registration details for verified accounts.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">1.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers.</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform, click patterns.</li>
                <li><strong>Location Data:</strong> Approximate location based on IP address; precise location if you enable location services.</li>
                <li><strong>Cookies and Tracking:</strong> See our <Link to="/cookies" className="text-purple-400 hover:underline">Cookie Policy</Link> for details.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">1.3 Information from Third Parties</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong>Social Login:</strong> Profile information from Google, Facebook, or other social accounts if you choose to sign in with them.</li>
                <li><strong>Partners:</strong> Information from venues, event organizers, and other partners related to bookings and events.</li>
                <li><strong>Public Sources:</strong> Publicly available information relevant to verification or fraud prevention.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section id="section-2" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Eye className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">2. How We Use Your Information</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li><strong>Service Provision:</strong> To provide, maintain, and improve our Services, including processing transactions and bookings.</li>
              <li><strong>Personalization:</strong> To customize your experience, recommend content, events, and connections.</li>
              <li><strong>Communication:</strong> To send service notifications, updates, security alerts, and promotional content (with your consent).</li>
              <li><strong>Safety & Security:</strong> To detect and prevent fraud, abuse, and security threats.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
              <li><strong>Analytics:</strong> To understand how users interact with our Services and improve them.</li>
              <li><strong>Support:</strong> To respond to your inquiries and provide customer support.</li>
              <li><strong>Marketing:</strong> To promote our Services and partners' offerings (with your consent where required).</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section id="section-3" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">3. Information Sharing and Disclosure</h2>
          
          <div className="space-y-6 text-gray-300">
            <p>We may share your information in the following circumstances:</p>
            
            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.1 With Your Consent</h3>
              <p className="text-gray-400">When you explicitly authorize us to share your information with third parties.</p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.2 Service Providers</h3>
              <p className="text-gray-400">With trusted third-party vendors who assist us in operating our Services (payment processors, cloud hosting, analytics, customer support).</p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.3 Business Partners</h3>
              <p className="text-gray-400">With venues, event organizers, and other partners to facilitate bookings and services you request.</p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.4 Legal Requirements</h3>
              <p className="text-gray-400">When required by law, legal process, or government request; to enforce our terms; or to protect rights, property, or safety.</p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.5 Business Transfers</h3>
              <p className="text-gray-400">In connection with mergers, acquisitions, or sale of assets, your information may be transferred to the acquiring entity.</p>
            </div>

            <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <p className="text-purple-300 font-medium">We do not sell your personal information to third parties for their marketing purposes.</p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="section-4" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">4. Data Security</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>We implement robust security measures to protect your information:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure authentication mechanisms including multi-factor authentication</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls limiting employee access to personal data</li>
              <li>Incident response procedures for potential data breaches</li>
            </ul>
            <p className="text-gray-400">
              While we strive to protect your information, no method of transmission over the Internet or electronic 
              storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>
        </section>

        {/* Section 5 */}
        <section id="section-5" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>We retain your information for as long as necessary to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Provide our Services to you</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Improve our Services through aggregated analytics</li>
            </ul>
            <p className="text-gray-400">
              When you delete your account, we will delete or anonymize your personal information within 30 days, 
              except where retention is required by law or legitimate business purposes.
            </p>
          </div>
        </section>

        {/* Section 6 */}
        <section id="section-6" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">6. Your Rights and Choices</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your data</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-400">
              To exercise these rights, please contact us at <a href="mailto:privacy@metrocitygroove.online" className="text-purple-400 hover:underline">privacy@metrocitygroove.online</a>.
              We will respond within 30 days. For EU/EEA residents, see our <Link to="/gdpr" className="text-purple-400 hover:underline">GDPR Compliance</Link> page.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section id="section-7" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">7. International Data Transfers</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>
              Metro City Groove is based in South Africa. Your information may be transferred to, stored, and 
              processed in countries other than your country of residence. We ensure appropriate safeguards are 
              in place for international transfers, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Standard Contractual Clauses approved by relevant authorities</li>
              <li>Compliance with POPIA (Protection of Personal Information Act) in South Africa</li>
              <li>Compliance with GDPR for EU/EEA data subjects</li>
              <li>Data processing agreements with all service providers</li>
            </ul>
          </div>
        </section>

        {/* Section 8 */}
        <section id="section-8" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">8. Children's Privacy</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              Our Services are not intended for users under 18 years of age. We do not knowingly collect personal 
              information from children under 18. If we discover that we have collected information from a child 
              under 18, we will delete it promptly. If you believe a child has provided us with personal information, 
              please contact us immediately.
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section id="section-9" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">9. Third-Party Services</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              Our Services may contain links to third-party websites, apps, and services. This Privacy Policy does 
              not apply to those third parties. We encourage you to review the privacy policies of any third-party 
              services you access through our platform.
            </p>
            <p className="text-gray-400">
              We integrate with third-party services including payment processors, analytics providers, and social 
              media platforms. These integrations are governed by their respective privacy policies.
            </p>
          </div>
        </section>

        {/* Section 10 */}
        <section id="section-10" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">10. Changes to This Policy</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Posting the updated policy on our platform with a new "Last Updated" date</li>
              <li>Sending you an email notification for significant changes</li>
              <li>Displaying a prominent notice within the Services</li>
            </ul>
            <p className="text-gray-400">
              Your continued use of our Services after the effective date of changes constitutes acceptance of the 
              updated policy.
            </p>
          </div>
        </section>

        {/* Section 11 */}
        <section id="section-11" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">11. Contact Us</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
            <div className="p-6 bg-white/5 rounded-xl border border-white/10 space-y-3">
              <p><strong>Metro City Trading (Pty) Ltd</strong></p>
              <p className="text-gray-400">Data Protection Officer</p>
              <p className="text-gray-400">Email: <a href="mailto:privacy@metrocitygroove.online" className="text-purple-400 hover:underline">privacy@metrocitygroove.online</a></p>
              <p className="text-gray-400">General: <a href="mailto:info@metrocitygroove.online" className="text-purple-400 hover:underline">info@metrocitygroove.online</a></p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/terms" className="text-purple-400 hover:underline">Terms of Service</Link>
            <Link to="/cookies" className="text-purple-400 hover:underline">Cookie Policy</Link>
            <Link to="/gdpr" className="text-purple-400 hover:underline">GDPR Compliance</Link>
          </div>
        </section>
      </main>
    </div>;
};
export default PrivacyPolicy;